import { type FC, useState, useMemo, useCallback, useEffect } from 'react';
import { Modal, Input, Radio, Checkbox, Spin, Button } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { tpoOptionTestId, tpoPartTestId } from '../../../config/testIds';
import type { PickerPlan } from '../../types';

type SelectionMode = 'single' | 'multiple';

interface PlanSelectionModalProps {
    open: boolean;
    title: string;
    subtitle: string;
    confirmLabel: string;
    selectionMode: SelectionMode;
    plans: PickerPlan[];
    isLoading?: boolean;
    maxSelections?: number;
    currentSingleSelection?: string | null;
    currentMultiSelections?: string[];
    excludePlanIds?: string[];
    onConfirm: (selectedPlanIds: string[]) => void;
    onCancel: () => void;
    testId: string;
}

const PlanSelectionModal: FC<PlanSelectionModalProps> = ({
    open,
    title,
    subtitle,
    confirmLabel,
    selectionMode,
    plans,
    isLoading,
    maxSelections,
    currentSingleSelection,
    currentMultiSelections,
    excludePlanIds,
    onConfirm,
    onCancel,
    testId,
}) => {
    const { labels } = useTpoGenConfig();
    const modalLabels = labels.comparisonReport.createReportModal;

    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) {
            setSearch('');
            if (selectionMode === 'single') {
                setSelectedId(currentSingleSelection ?? null);
            } else {
                setSelectedIds(new Set(currentMultiSelections ?? []));
            }
        }
    }, [open, selectionMode, currentSingleSelection, currentMultiSelections]);

    const visiblePlans = useMemo(() => {
        let result = plans;
        if (excludePlanIds && excludePlanIds.length > 0) {
            const excluded = new Set(excludePlanIds);
            result = result.filter((p) => !excluded.has(p.id));
        }
        if (search) {
            const lower = search.toLowerCase();
            result = result.filter((p) => p.plan_name.toLowerCase().includes(lower));
        }
        return result;
    }, [plans, excludePlanIds, search]);

    const handleToggleCheckbox = useCallback(
        (id: string) => {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    if (maxSelections && next.size >= maxSelections) return prev;
                    next.add(id);
                }
                return next;
            });
        },
        [maxSelections],
    );

    const handleConfirm = useCallback(() => {
        if (selectionMode === 'single') {
            if (selectedId) onConfirm([selectedId]);
        } else {
            onConfirm([...selectedIds]);
        }
    }, [selectionMode, selectedId, selectedIds, onConfirm]);

    const isConfirmDisabled = selectionMode === 'single'
        ? !selectedId
        : selectedIds.size === 0;

    const isAtMax = selectionMode === 'multiple' && !!maxSelections && selectedIds.size >= maxSelections;

    const planList = (
        <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
            {visiblePlans.map((plan) => {
                const isSelected = selectionMode === 'single'
                    ? plan.id === selectedId
                    : selectedIds.has(plan.id);

                return (
                    <div
                        key={plan.id}
                        className={`flex items-center px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                                ? 'bg-[#F7F5FF] border-[#44338A]'
                                : 'border-[#E4E4E7] bg-white hover:bg-[#FAFAFA]'
                        }`}
                        onClick={() => {
                            if (selectionMode === 'single') {
                                setSelectedId(plan.id);
                            } else {
                                handleToggleCheckbox(plan.id);
                            }
                        }}
                        data-testid={tpoOptionTestId(testId, plan.id)}
                    >
                        {selectionMode === 'single' ? (
                            <Radio checked={isSelected} className="!mr-3" />
                        ) : (
                            <Checkbox checked={isSelected} className="!mr-3" />
                        )}
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-[#18181B]">
                                {plan.plan_name}
                            </span>
                            {(plan.created_by || plan.year) ? (
                                <span className="text-xs text-[#A1A1AA]">
                                    {modalLabels.createdByLabel(
                                        plan.created_by ?? '',
                                        String(plan.year ?? ''),
                                    )}
                                </span>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            {!isLoading && visiblePlans.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#A1A1AA]" data-testid={tpoPartTestId(testId, 'empty')}>
                    {labels.common.noData}
                </div>
            ) : null}
        </div>
    );

    const footer = (
        <div className="flex items-center justify-end gap-2">
            <Button onClick={onCancel} data-testid={tpoPartTestId(testId, 'cancel_btn')}>
                {labels.common.cancel}
            </Button>
            <Button
                type="primary"
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                data-testid={tpoPartTestId(testId, 'confirm_btn')}
            >
                {confirmLabel}
            </Button>
        </div>
    );

    return (
        <Modal
            title={
                <div className="flex flex-col gap-1">
                    <span className="text-base font-semibold">{title}</span>
                    <span className="text-sm font-normal text-[#71717A]">{subtitle}</span>
                    {isAtMax ? (
                        <div className="flex items-center gap-2 mt-1" data-testid={tpoPartTestId(testId, 'max_warning')}>
                            <ExclamationCircleOutlined className="text-sm shrink-0" style={{ color: '#ff4d4f' }} />
                            <span className="text-xs text-[#ff4d4f]">{modalLabels.maxLimitReached}</span>
                        </div>
                    ) : null}
                </div>
            }
            open={open}
            footer={footer}
            width={720}
            closable={false}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
        >
            <div className="flex flex-col gap-3" data-testid={testId}>
                <Input
                    placeholder={modalLabels.search}
                    suffix={<SearchOutlined className="text-[#A1A1AA]" />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    data-testid={tpoPartTestId(testId, 'search_input')}
                />
                <Spin spinning={!!isLoading}>{planList}</Spin>
            </div>
        </Modal>
    );
};

PlanSelectionModal.displayName = 'PlanSelectionModal';
export default PlanSelectionModal;
